import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Play, CheckCircle, Mail } from "lucide-react";
import { z } from "zod";
import { getStoredAffiliateCode } from "@/hooks/useAffiliateTracking";

const emailSchema = z.string().email("Email invalide").max(255, "Email trop long");

interface TestRequestFormProps {
  trigger?: React.ReactNode;
}

const TestRequestForm = ({ trigger }: TestRequestFormProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate email
    const validation = emailSchema.safeParse(email);
    if (!validation.success) {
      setError(validation.error.errors[0].message);
      return;
    }

    setLoading(true);
    const trimmedEmail = email.trim().toLowerCase();

    try {
      // Get affiliate code from localStorage if present
      const affiliateCode = getStoredAffiliateCode();
      
      console.log("📝 Submitting test request for:", trimmedEmail);
      
      // Call the centralized edge function that handles everything server-side
      // This includes: anti-abuse checks (IP + email), insert, and all emails
      const { data, error: invokeError } = await supabase.functions.invoke("submit-test-request", {
        body: {
          email: trimmedEmail,
          affiliateCode,
        },
      });

      if (invokeError) {
        console.error("❌ Submit error:", invokeError);
        throw invokeError;
      }
      
      console.log("✅ Request submitted successfully:", data);

      setSuccess(true);
      toast.success("Demande de test enregistrée !");
    } catch (err: any) {
      console.error("❌ Form submission error:", err);
      toast.error("Erreur lors de l'envoi de la demande");
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset after animation
    setTimeout(() => {
      setEmail("");
      setSuccess(false);
      setError("");
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
            <Play className="w-4 h-4 mr-2" />
            Essayer 24h gratuitement
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-card border-border text-foreground max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">Essai gratuit 24h</DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="text-center py-4 space-y-4">
            <div className="mx-auto w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-secondary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Demande envoyée !</h3>
              <p className="text-muted-foreground mt-2">
                Vous recevrez vos identifiants par email très bientôt.
              </p>
            </div>
            
            {/* Alerte email importante */}
            <div className="bg-amber-500/20 border-2 border-amber-500 rounded-xl p-3 text-left">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="font-bold text-amber-600 text-sm">
                    📧 Vérifiez vos emails ET vos spams !
                  </p>
                  <p className="text-xs text-amber-700">
                    L'email peut arriver dans les spams.
                  </p>
                </div>
              </div>
            </div>
            
            <Button onClick={handleClose} className="w-full bg-primary hover:bg-primary/90">
              Fermer
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-muted-foreground">
              Entrez votre email pour recevoir vos identifiants de test gratuit pendant 24h.
            </p>
            <div className="space-y-2">
              <Label htmlFor="test-email">Votre email</Label>
              <Input
                id="test-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="bg-input border-border"
                required
              />
              {error && <p className="text-destructive text-sm">{error}</p>}
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {loading ? "Prise en charge de votre commande..." : "Demander mon essai gratuit"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              En soumettant ce formulaire, vous acceptez de recevoir vos identifiants par email.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TestRequestForm;
